import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ApiAuthenticateResponseModel} from '../model/api-authenticate-response.model';
import {Observable, Subject, Subscriber, tap} from 'rxjs';
import {environment} from '../../environment';
import {AdministratorApiAuthenticateRequestModel} from '../model/administrator-api-authenticate-request.model';
import {SessionStorageService} from './session-storage.service';
import {AdministratorApiModel} from '../model/administrator-api.model';
import {PeoApiCredentialModel} from '../model/peo-api-credential.model';
import {AdminEvent} from '../event/admin.event';
import {AdminEventTypeEnum} from '../enum/admin-event-type.enum';

/**
 * @class
 * @description This class provides methods to interact with the Admin API.
 * @export
 * @name AdminService
 * @service
 */
@Injectable({

  // We'll want this service to be provided as a singleton.
  providedIn: 'root'
})
export class AdminService {

  /**
   * @description This constant property contains the unique identifying name of the Admin session in the session storage provider.
   * @memberof AdminService
   * @name AdminService.SessionStorageServiceKey
   * @property
   * @public
   * @readonly
   * @static
   * @type {string}
   */
  public static readonly SessionStorageServiceKey: string = 'syncstream-admin-session';

  /**
   * @description This property contains the event source for the administrator service provider.
   * @memberof AdminService
   * @name AdminService#eventSource
   * @private
   * @property
   * @type {Subject<AdminEvent>}
   */
  private eventSource: Subject<AdminEvent> = new Subject<AdminEvent>();

  /**
   * @description This property contains the interval for the Administrator session expiration.
   * @memberof AdminService
   * @name AdminService#intervalSessionExpired
   * @private
   * @property
   * @type {NodeJS.Timeout}
   */
  private intervalSessionExpired: any = null;

  /**
   * @description This property contains the interval for the impending Administrator session expiration.
   * @memberof AdminService
   * @name AdminService#intervalSessionExpiring
   * @private
   * @property
   * @type {NodeJS.Timeout}
   */
  private intervalSessionExpiring: any = null;

  /**
   * @description This property contains the events observable for the administrator service provider.
   * @memberof AdminService
   * @name AdminService#events
   * @public
   * @property
   * @type {Observable<AdminEvent>}
   */
  public events: Observable<AdminEvent> = this.eventSource.asObservable();

  /**
   * @constructor
   * @description This method instantiates a new instance of our Admin service provider with injected dependencies.
   * @param {HttpClient} httpClient The injected instance of our HTTP Client service provider.
   * @param {SessionStorageService} sessionStorageService The injected instance of our session storage service provider.
   * @memberof AdminService
   * @name AdminService#constructor
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our HTTP Client service provider.
     * @memberof AdminService
     * @name AdminService#httpClient
     * @private
     * @readonly
     * @type {HttpClient}
     */
    private readonly httpClient: HttpClient,
    /**
     * @description This property contains the injected instance of our session storage service provider.
     * @memberof AdminService
     * @name AdminService#sessionStorageService
     * @private
     * @readonly
     * @type {SessionStorageService}
     */
    private readonly sessionStorageService: SessionStorageService
  ) {
  }

  /**
   * @description This method clears the current session from the session storage.
   * @memberof AdminService
   * @method
   * @name AdminService#clearSession
   * @private
   * @returns {void}
   */
  private clearSession(): void {

    // Reset the session expiration interval if it exists.
    if (this.intervalSessionExpired) {

      // Clear the session expiration interval.
      clearInterval(this.intervalSessionExpired);

      // Reset the session expiration interval.
      this.intervalSessionExpired = null;
    }

    // Reset the session expiring interval if it exists.
    if (this.intervalSessionExpiring) {

      // Clear the session expiring interval.
      clearInterval(this.intervalSessionExpiring);

      // Reset the session expiring interval.
      this.intervalSessionExpiring = null;
    }

    // Clear the session from the session storage.
    this.sessionStorageService.delete(AdminService.SessionStorageServiceKey);
  }

  /**
   * @description This method emits an event for the administrator service provider.
   * @memberof AdminService
   * @method
   * @name AdminService#emitEvent
   * @param {AdminEvent} event The event to emit.
   * @private
   * @returns {void}
   */
  private emitEvent(event: AdminEvent): void {

    // Check for an event source then emit the event.
    if (this.eventSource) this.eventSource.next(event);
  }

  /**
   * @async
   * @description This method asynchronously deletes an administrator from the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#delete
   * @param {AdministratorApiModel | string} modelOrEmailAddress The administrator or the email address of the administrator to delete from the .NET API.
   * @returns {Observable<AdministratorApiModel>} An observable containing the freshly deleted administrator.
   * @throws {Error} When no current session exists.
   */
  public delete(modelOrEmailAddress: AdministratorApiModel | string): Observable<AdministratorApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.delete<AdministratorApiModel>(
      // The normalized URL of the .NET Backend API which processes the request.
      environment.normalizedApiUrl('/api/administrator/:emailAddress', {

        // Pass the email address of the administrator to the .NET API.
        emailAddress: (typeof modelOrEmailAddress === 'string' ? modelOrEmailAddress : modelOrEmailAddress.emailAddress)?.trim()
      }),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously deletes many administrators from the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#deleteMany
   * @param {(AdministratorApiModel | string)[]} modelsOrEmailAddresses The administrators or the email addresses of the administrators to delete from the .NET API.
   * @public
   * @returns {Observable<AdministratorApiModel[]>} An observable containing the freshly deleted administrators.
   * @throws {Error} When no current session exists.
   */
  public deleteMany(modelsOrEmailAddresses: (AdministratorApiModel | string)[]): Observable<AdministratorApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<AdministratorApiModel[]>(
      // The normalized URL of the .NET Backend API which processes the request.
      environment.normalizedApiUrl('/api/administrator/delete'),

      // Map the email addresses from the models or email addresses.
      modelsOrEmailAddresses.map((v: AdministratorApiModel | string): string => (typeof v === 'string' ? v : v.emailAddress)?.trim()),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously retrieves the details for the authenticated administrator session from the .NET Backend API which authenticates the request back to PrismHR.
   * @memberof AdminService
   * @method
   * @name AdminService#details
   * @public
   * @returns {Observable<AdministratorApiModel>} An observable containing the response from the .NET Backend API which authenticates the request back to PrismHR.
   */
  public details(): Observable<AdministratorApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<AdministratorApiModel>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/administrator/details'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      }
    );
  }

  /**
   * @async
   * @description This method asynchronously lists the administrators from the .NET Admin API.
   * @memberof AdminService
   * @method
   * @name AdminService#list
   * @public
   * @returns {Observable<AdministratorApiModel[]>} An observable containing the response from the .NET Backend API.
   * @throws {Error} When no current session exists.
   */
  public list(): Observable<AdministratorApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<AdministratorApiModel[]>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/administrator/list'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously lists the PEO API credentials from the .NET Admin API.
   * @memberof AdminService
   * @method
   * @name AdminService#listPeoApiCredentials
   * @public
   * @returns {Observable<PeoApiCredentialModel[]>} An observable containing the response from the .NET Backend API.
   * @throws {Error} When no current session exists.
   */
  public listPeoApiCredentials(): Observable<PeoApiCredentialModel[]> {

    // We're done, return the observable from the HTTP client service provider containing the response from the .NET API.
    return this.httpClient.get<PeoApiCredentialModel[]>(
      // The URL of the .NET Backend API.
      environment.normalizedApiUrl('/api/administrator/peo-api-credentials'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously locks an administrator from the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#lock
   * @param {AdministratorApiModel | string} modelOrEmailAddress The administrator or the email address of the administrator to lock from the .NET API.
   * @public
   * @returns {Observable<AdministratorApiModel>} An observable containing the freshly locked administrator.
   * @throws {Error} When no current session exists.
   */
  public lock(modelOrEmailAddress: AdministratorApiModel | string): Observable<AdministratorApiModel> {

    // We're done, return the observable from the HTTP client service provider containing the response from the .NET API.
    return this.httpClient.patch<AdministratorApiModel>(
      // The URL of the .NET Backend API.
      environment.normalizedApiUrl('/api/administrator/:emailAddress/lock', {

        // Pass the email address of the administrator to the .NET API.
        emailAddress: (typeof modelOrEmailAddress === 'string' ? modelOrEmailAddress : modelOrEmailAddress.emailAddress)?.trim()
      }),

      // We're not sending a body with singular locks.
      null,

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously locks many administrators from the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#lockMany
   * @param {(AdministratorApiModel | string)[]} modelsOrEmailAddresses The administrators or the email addresses of the administrators to lock from the .NET API.
   * @public
   * @returns {Observable<AdministratorApiModel[]>} An observable containing the freshly locked administrators.
   * @throws {Error} When no current session exists.
   */
  public lockMany(modelsOrEmailAddresses: (AdministratorApiModel | string)[]): Observable<AdministratorApiModel[]> {

    // We're done, return the observable from the HTTP client service provider containing the response from the .NET API.
    return this.httpClient.patch<AdministratorApiModel[]>(
      // The URL of the .NET Backend API.
      environment.normalizedApiUrl('/api/administrator/lock'),

      // Map the email addresses from the models or email addresses.
      modelsOrEmailAddresses.map((v: AdministratorApiModel | string): string => (typeof v === 'string' ? v : v.emailAddress)?.trim()),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @description This method determines whether an administrator is online or not.
   * @memberof AdminService
   * @method
   * @name AdminService#online
   * @public
   * @returns {boolean}
   */
  public online(): boolean {

    // We're done, return whether the session is online and unexpired or not.
    return (this.sessionStorageService.online(AdminService.SessionStorageServiceKey) &&
      !this.sessionStorageService.expired(AdminService.SessionStorageServiceKey));
  }

  /**
   * @async
   * @description This method asynchronously saves an administrator to the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#save
   * @param {AdministratorApiModel} model The administrator to save to the .NET API.
   * @returns {Observable<AdministratorApiModel>} An observable containing the freshly saved administrator.
   * @throws {Error} When no current session exists.
   */
  public save(model: AdministratorApiModel): Observable<AdministratorApiModel> {

    // We're done, return the observable from the HTTP client service provider containing the response from the .NET API.
    return this.httpClient.post<AdministratorApiModel>(
      // The URL of the .NET Backend API.
      environment.normalizedApiUrl('/api/administrator'),

      // Define the body of the request.
      model,

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously saves many administrators to the .NET API.
   * @memberof AdminService
   * @method
   * @name AdminService#saveMany
   * @param {AdministratorApiModel[]} models The administrators to save to the .NET API.
   * @returns {Observable<AdministratorApiModel[]>} An observable containing the freshly saved administrators.
   * @throws {Error} When no current session exists.
   */
  public saveMany(models: AdministratorApiModel[]): Observable<AdministratorApiModel[]> {

    // We're done, return the observable from the HTTP client service provider containing the response from the .NET API.
    return this.httpClient.post<AdministratorApiModel[]>(
      // The URL of the .NET Backend API.
      environment.normalizedApiUrl('/api/administrator/batch'),

      // Define the body of the request.
      models,

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @description This method ensures that a currently authenticated administrator session exists.
   * @memberof AdminService
   * @method
   * @name AdminService#session()
   * @public
   * @returns {ApiAuthenticateResponseModel} The current authenticated administrator session.
   * @throws {Error} When there isn't currently an authenticated administrator session.
   */
  public session(): ApiAuthenticateResponseModel {

    // Localize the existing session from the session storage provider.
    const session: ApiAuthenticateResponseModel | undefined =
      this.sessionStorageService.load<ApiAuthenticateResponseModel>(AdminService.SessionStorageServiceKey);

    // Ensure we have a session to sign out, throw an error if we don't.
    if (!session) throw new Error('No existing administrator session.');

    // Ensure the session hasn't expired.
    if (this.sessionStorageService.expired<ApiAuthenticateResponseModel>(AdminService.SessionStorageServiceKey)) {

      // Clear any existing session data.
      this.clearSession();

      // Fire the session-expired event.
      this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionExpired));

      // We're done, throw an error indicating the session has expired.
      throw new Error('The existing administrator session has expired.');
    }

    // We're done, return the session.
    return session;
  }

  /**
   * @async
   * @description This method asynchronously authenticates an incoming admin request with the .NET Admin API.
   * @memberof AdminService
   * @method
   * @name AdminService#signIn
   * @param {string} emailAddress The email address of the administrator.
   * @param {string} password The password of the administrator.
   * @param {boolean} [persistent] An optional flag denoting session persistence.
   * @public
   * @returns {Observable<ApiAuthenticateResponseModel>} An observable containing the response from the .NET Backend API.
   */
  public signIn(emailAddress: string, password: string, persistent: boolean = false): Observable<ApiAuthenticateResponseModel> {

    // Try to localize any existing session from storage.
    try {

      // Localize the session from the session storage provider.
      const session: ApiAuthenticateResponseModel = this.session();

      // Emit our session-continued event.
      this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionContinued));

      // We're done, resolve the observable with the existing session from storage.
      return new Observable<ApiAuthenticateResponseModel>(
        (subscriber: Subscriber<ApiAuthenticateResponseModel>) => subscriber.next(session));
    }

      // Handle any exceptions that occur during the session-continuation process.
    catch (_) {

      // Clear any existing session data.
      this.clearSession();
    }

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<ApiAuthenticateResponseModel>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/administrator/sign-in'),

      // The body of the request.
      {
        // Set the email address of the administrator into the request body.
        emailAddress,

        // Set the password of the administrator into the request body.
        password,

        // Set the persistent session flag into the request body.
        persistent,
      } as AdministratorApiAuthenticateRequestModel
    ).pipe(tap({

      // Handle the completion of the sign-in process.
      complete: (): void => {

        // Set the session timeout interval into the instance.
        this.intervalSessionExpired = setInterval(() =>
            this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionExpired)),
          this.sessionStorageService.expiresIn(AdminService.SessionStorageServiceKey));

        // Set the impending session timeout interval into the instance.
        this.intervalSessionExpiring = setInterval(() =>
            this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionExpiring)),
          this.sessionStorageService.expiresIn(AdminService.SessionStorageServiceKey) - 90000);

        // We're done, emit the session-start event.
        this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionStart));
      },

      // Handle any errors that occur during the sign-in process.
      error: (_: Error): void => this.clearSession(),

      // Handle the session creation.
      next: (r: ApiAuthenticateResponseModel): void => {

        // Clear any existing session storage.
        this.clearSession();

        // Store the authenticated administrator session in the session storage.
        this.sessionStorageService.save(AdminService.SessionStorageServiceKey, r);
      }
    }));
  }

  /**
   * @async
   * @description This method asynchronously signs out the current authenticated Admin session with the .NET Backend API.
   * @memberof AdminService
   * @method
   * @name AdminService#signOut
   * @public
   * @returns {Observable<void>} An observable containing the response from the .NET Backend API.
   * @throws {Error} When no current session exists.
   */
  public signOut(): Observable<void> {

    // Ensure we've got an active administrator session.
    this.session();

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<void>(
      // The URL of the .NET Backend API which authenticates the request back to PrismHR.
      environment.normalizedApiUrl('/api/administrator/sign-out'),

      // The options of the request.
      {

        // Set the with credentials flag into the request options.
        withCredentials: true
      }).pipe(tap({

        // Handle the completion of the sign-out process.
        complete: (): void => {

          // Clear any existing session data.
          this.clearSession();

          // We're done, emit the session-end event.
          this.emitEvent(new AdminEvent(AdminEventTypeEnum.SessionEnd));
        }
      }
    ));
  }
}
